import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, Users, Award, Clock, Briefcase, Heart, 
  Shield, Star, CheckCircle, Linkedin, X
} from 'lucide-react';
import { Layout } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { teamMembers } from '@/data/navigation';
import SEO from '@/components/shared/SEO';
import ScheduleCallModal from '@/components/modals/ScheduleCallModal';

// Process steps
const processSteps = [
  {
    number: '01',
    title: 'Tell us what matters most',
    description: 'Safety, durability, appearance, or cost—your goals guide everything we do.'
  },
  {
    number: '02',
    title: 'We evaluate your spaces',
    description: 'Our experts identify potential issues before they become problems.'
  },
  {
    number: '03',
    title: 'You get a complete solution',
    description: 'From installation (carpet, LVT, tile, grid matting, sports flooring) to maintenance programs that keep your floors performing and looking their best.'
  }
];

// Differentiators
const differentiators = [
  {
    icon: Heart,
    title: 'Concierge Services',
    description: 'Personalized attention from dedicated account managers who understand your business.'
  },
  {
    icon: Users,
    title: 'Expert Team Members',
    description: 'Industry veterans with decades of combined experience in commercial flooring solutions.'
  },
  {
    icon: Shield,
    title: 'Quality Guarantee',
    description: 'We stand behind every installation with comprehensive warranties and support.'
  }
];

const AboutUsPage = () => {
  const [isCallModalOpen, setIsCallModalOpen] = useState(false);
  const [isHiringModalOpen, setIsHiringModalOpen] = useState(false);
  const navigate = useNavigate();
  return (
    <Layout>
      <SEO 
        title="About Us"
        description="Learn about Portico Systems - your trusted partner in commercial flooring solutions since 1987. Discover our mission, values, and the expert team behind our premium flooring products."
        canonicalUrl="/about"
      />
      {/* Hero Section */}
      <section className="relative min-h-[400px] md:min-h-[500px] flex items-center">
        <div className="absolute inset-0 z-0">
          <img
            src="/Assets/images/02_About-Us/feature_hero-image.jpg"
            alt="Portico Systems Team"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-bg/90 to-primary-bg/70" />
        </div>
        <div className="container-portico relative z-10 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl"
          >
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6">
              About <span className="whitespace-nowrap">Portico Systems</span>
            </h1>
            <p className="text-xl text-primary-foreground/90">
              25+ years of delivering performance flooring solutions that protect your investment and elevate your brand.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section id="our-story" className="section-padding bg-background">
        <div className="container-portico">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src="/Assets/images/02_About-Us/3_amigos.jpg"
                alt="Portico Systems Leadership Team"
                className="rounded-2xl shadow-xl"
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
                Flooring That Works As Hard As You Do
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  At Portico Systems, we understand that your flooring does more than just cover the ground. It shapes how customers feel when they walk through your doors and ultimately impacts your success. For nearly 25 years, we've worked alongside some of America's most trusted retailers, including Target, Albertsons, and Ace Hardware, helping them create spaces that work as hard as they do.
                </p>
                <p>
                  We know that managing flooring across multiple locations can feel overwhelming. That's why we've built our approach around making your life easier. We bring together product procurement, installation, and maintenance into one streamlined process, so you have fewer vendors to manage and more time to focus on what matters most.
                </p>
                <p>
                  What sets us apart: we listen first. We want to understand your priorities and what success looks like for you. Then we design a solution that actually solves those problems in the day to day reality of running your business.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="our-process" className="section-padding bg-muted">
        <div className="container-portico">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Our Process & Service
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              We've refined our approach to deliver seamless experiences and exceptional results.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              {processSteps.map((step, index) => (
                <motion.div
                  key={step.number}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex gap-6"
                >
                  <div className="flex-shrink-0 w-16 h-16 bg-primary rounded-xl flex items-center justify-center border-2 border-white shadow-md">
                    <span className="font-heading font-bold text-xl text-primary-foreground">
                      {step.number}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-xl mb-2">{step.title}</h3>
                    <p className="text-muted-foreground">{step.description}</p>
                  </div>
                </motion.div>
              ))}

              <Button
                size="lg"
                className="mt-8"
                onClick={() => setIsCallModalOpen(true)}
                data-testid="schedule-call-cta-about"
              >
                Schedule a Call
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src="/Assets/images/02_About-Us/feature_our-process-service.jpg"
                alt="Our Process"
                className="rounded-2xl shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Differentiators Section */}
      <section id="what-sets-us-apart" className="section-padding bg-background">
        <div className="container-portico">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              What Sets Us Apart
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              The Portico difference is built on expertise, service, and commitment.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                image: '/Assets/images/02_About-Us/feature_conceirge-services.jpg',
                title: 'Concierge Services',
                description: 'Personalized attention from dedicated account managers who understand your business.'
              },
              {
                image: '/Assets/images/02_About-Us/feature_Expert-Team-Members.jpg',
                title: 'Expert Team Members',
                description: 'Industry veterans with decades of combined experience in commercial flooring solutions.'
              },
              {
                image: '/Assets/images/02_About-Us/feature_quality-guarentee_02.jpg',
                title: 'Quality Guarantee',
                description: 'We stand behind every installation with comprehensive warranties and support.'
              }
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-portico-elevated overflow-hidden"
              >
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6 text-center">
                  <h3 className="font-heading font-semibold text-xl mb-3">{item.title}</h3>
                  <p className="text-muted-foreground">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sales Team Section */}
      <section id="our-team" className="section-padding bg-secondary text-secondary-foreground">
        <div className="container-portico">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Our Sales Team
            </h2>
            <p className="text-secondary-foreground/80 max-w-2xl mx-auto">
              Dedicated professionals ready to help you find the perfect flooring solution.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {teamMembers.sales.map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-card text-card-foreground rounded-xl p-6 text-center"
              >
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-24 h-24 rounded-full mx-auto mb-4 object-cover border-4"
                  style={{ borderColor: '#003b71', ...(member.imagePosition ? { objectPosition: member.imagePosition } : {}) }}
                />
                <h4 className="font-semibold text-lg">{member.name}</h4>
                <p className="text-sm text-muted-foreground mb-3">{member.title}</p>
                <div className="flex justify-center gap-2">
                  <a
                    href={member.linkedin}
                    className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors"
                  >
                    <Linkedin className="w-4 h-4" />
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Careers CTA */}
      <section id="careers" className="section-padding bg-muted">
        <div className="container-portico">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-card rounded-2xl p-8 md:p-12 text-center border border-border max-w-3xl mx-auto"
          >
            <Briefcase className="w-12 h-12 text-primary mx-auto mb-6" />
            <h2 className="font-heading text-2xl md:text-3xl font-bold mb-4">
              We're Hiring!
            </h2>
            <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
              Join our team of flooring experts and help businesses transform their spaces. 
              We're looking for passionate individuals who share our commitment to excellence.
            </p>
            <Button
              variant="secondary"
              size="lg"
              onClick={() => setIsHiringModalOpen(true)}
              data-testid="hiring-cta-button"
            >
              View Open Positions
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Hiring Modal */}
      <Dialog open={isHiringModalOpen} onOpenChange={setIsHiringModalOpen}>
        <DialogContent className="sm:max-w-md" data-testid="hiring-modal">
          <DialogHeader>
            <div className="flex justify-center mb-4">
              <Briefcase className="w-10 h-10 text-primary" />
            </div>
            <DialogTitle className="text-center text-xl font-bold">
              We're Hiring!
            </DialogTitle>
            <DialogDescription className="text-center pt-3 text-sm leading-relaxed">
              Thanks for your interest in joining our team! While we may not list all open roles publicly, we're always looking for talented individuals. Reach out to our HR team to learn more about current and upcoming opportunities.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center pt-4">
            <Button
              size="lg"
              onClick={() => {
                window.location.href = 'mailto:hr@porticosystems.com';
                setIsHiringModalOpen(false);
              }}
              data-testid="hiring-contact-hr-button"
            >
              Contact HR — hr@porticosystems.com
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ScheduleCallModal isOpen={isCallModalOpen} onClose={() => setIsCallModalOpen(false)} />
    </Layout>
  );
};

export default AboutUsPage;
